
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ResponsiveNavigation from '@/components/ResponsiveNavigation';

const Notifications = () => {
  const navigate = useNavigate();
  
  const [preferences, setPreferences] = useState({
    orderUpdates: true,
    newOfferings: true,
    newsletter: false,
    promotions: false,
    systemUpdates: true,
    invoiceReminders: true,
    stockAlerts: false,
    patientNotifications: true
  });

  const handleToggle = (key: keyof typeof preferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = () => {
    console.log('Saving notification preferences:', preferences);
    // Here you would typically save to backend
  };

  return (
    <div className="min-h-screen bg-gray-50 font-lato">
      <ResponsiveNavigation />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6 text-vitl-navy hover:text-vitl-green"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-syne font-semibold text-vitl-navy mb-2">
            Notification Preferences
          </h1>
          <p className="text-vitl-dark/70 font-lato">
            Manage your email notifications and communication preferences
          </p>
        </div>

        {/* Email Notifications */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-vitl-navy font-syne">Email Notifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Updates */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="order-updates" className="text-vitl-navy font-lato font-medium">
                  Order Updates
                </Label>
                <p className="text-sm text-vitl-dark/70 font-lato">
                  Get notified when order status changes, shipments, and delivery confirmations
                </p>
              </div>
              <Switch
                id="order-updates"
                checked={preferences.orderUpdates}
                onCheckedChange={() => handleToggle('orderUpdates')}
              />
            </div>

            {/* New Offerings */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="new-offerings" className="text-vitl-navy font-lato font-medium">
                  New Product Offerings
                </Label>
                <p className="text-sm text-vitl-dark/70 font-lato">
                  Be the first to know about new drugs and treatments in our catalogue
                </p>
              </div>
              <Switch
                id="new-offerings"
                checked={preferences.newOfferings}
                onCheckedChange={() => handleToggle('newOfferings')}
              />
            </div>

            {/* Newsletter */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="newsletter" className="text-vitl-navy font-lato font-medium">
                  Monthly Newsletter
                </Label>
                <p className="text-sm text-vitl-dark/70 font-lato">
                  Industry insights, platform updates, and healthcare trends
                </p>
              </div>
              <Switch
                id="newsletter"
                checked={preferences.newsletter}
                onCheckedChange={() => handleToggle('newsletter')}
              />
            </div>

            {/* Promotions */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="promotions" className="text-vitl-navy font-lato font-medium">
                  Promotions & Discounts
                </Label>
                <p className="text-sm text-vitl-dark/70 font-lato">
                  Special offers, volume discounts, and promotional campaigns
                </p>
              </div>
              <Switch
                id="promotions"
                checked={preferences.promotions}
                onCheckedChange={() => handleToggle('promotions')}
              />
            </div>

            {/* System Updates */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="system-updates" className="text-vitl-navy font-lato font-medium">
                  System Updates
                </Label>
                <p className="text-sm text-vitl-dark/70 font-lato">
                  Platform maintenance, new features, and important system announcements
                </p>
              </div>
              <Switch
                id="system-updates"
                checked={preferences.systemUpdates}
                onCheckedChange={() => handleToggle('systemUpdates')}
              />
            </div>

            {/* Invoice Reminders */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="invoice-reminders" className="text-vitl-navy font-lato font-medium">
                  Invoice Reminders
                </Label>
                <p className="text-sm text-vitl-dark/70 font-lato">
                  Payment due dates, overdue notices, and billing updates
                </p>
              </div>
              <Switch
                id="invoice-reminders"
                checked={preferences.invoiceReminders}
                onCheckedChange={() => handleToggle('invoiceReminders')}
              />
            </div>

            {/* Stock Alerts */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="stock-alerts" className="text-vitl-navy font-lato font-medium">
                  Stock Availability Alerts
                </Label>
                <p className="text-sm text-vitl-dark/70 font-lato">
                  Get notified when out-of-stock items become available again
                </p>
              </div>
              <Switch
                id="stock-alerts"
                checked={preferences.stockAlerts}
                onCheckedChange={() => handleToggle('stockAlerts')}
              />
            </div>

            {/* Patient Notifications */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="patient-notifications" className="text-vitl-navy font-lato font-medium">
                  Patient-Related Updates
                </Label>
                <p className="text-sm text-vitl-dark/70 font-lato">
                  Patient prescription requests, consultations, and appointment reminders
                </p>
              </div>
              <Switch
                id="patient-notifications"
                checked={preferences.patientNotifications}
                onCheckedChange={() => handleToggle('patientNotifications')}
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            className="bg-vitl-green hover:bg-vitl-green/90 text-white font-lato font-medium px-8"
          >
            Save Preferences
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Notifications;
